import axios from 'axios';
import {
	T,
	always,
	cond,
	equals,
	filter,
	find,
	findIndex,
	head,
	indexOf,
	isEmpty,
	join,
	last,
	map,
	max,
	not,
	propEq,
	split,
	take,
} from 'ramda';
import { Children, ReactElement } from 'react';

import { mentalHealthTopicConstants } from '@ui/routes/MemberMH/MemberMHTopicSelectionStep';

import { getServiceType } from '@helpers/MentalHealth/mentalHealth';

import { ApiEndpoints } from '@utils/endpoints';

import { MHTopic } from '@data/mh_topics';

import { FlowIdTypes } from '@folx-types/flow';
import { FlowStepData, FormData } from '@folx-types/flow-steps';

type GetPathForCurrentStep = (pathname: string) => string;
export const getPathForCurrentStep: GetPathForCurrentStep = (pathname) => {
	const parts = split('/', pathname);
	const firstPart = head(parts) ?? '';
	return firstPart;
};

type GetPathPrefixFromPathname = (pathname: string, path: string) => string;
export const getPathPrefixFromPathname: GetPathPrefixFromPathname = (pathname, path) => {
	const separator = '/';
	const pathStart = split(separator, path)[0];
	const rawParts = split(separator, pathname);
	const parts = filter((part) => not(isEmpty(part)), rawParts);
	const destinationIndex = indexOf(pathStart, parts);
	const prefixedParts = take(destinationIndex, parts);
	return join(separator, prefixedParts);
};

type GetStepForPath = (path: string, steps: FlowStepData[]) => FlowStepData | undefined;
export const getStepForPath: GetStepForPath = (path, steps) => {
	const step = find((step) => equals(getPathForCurrentStep(step.path), path), steps);
	return step;
};

type GetStepForKey = (steps: FlowStepData[], stepKey: string) => FlowStepData | undefined;
export const getStepForKey: GetStepForKey = (steps, stepKey) => {
	return find(propEq('stepKey', stepKey), steps);
};

type GetStepsForFlow = (routes: ReactElement) => FlowStepData[];
export const getStepsForFlow: GetStepsForFlow = (routes) => {
	return (
		Children.map<FlowStepData, ReactElement>(routes, ({ props: stepProps }) => {
			return {
				path: stepProps.path,
				stepKey: stepProps.stepKey,
				bailout: stepProps.bailout,
				skip: stepProps.skip,
				finalStep: stepProps.finalStep,
				secure: stepProps.secure,
				onboardingStep: stepProps.onboardingStep,
				intermediate: stepProps.intermediate,
				returnKey: stepProps.returnKey,
				skipIfWaived: stepProps.skipIfWaived,
			};
		}) ?? []
	);
};

type GetStepKeysForSteps = (steps: FlowStepData[]) => string[];
export const getStepKeysForSteps: GetStepKeysForSteps = (steps) =>
	map((step) => step.stepKey, steps);

type GetStepIndex = (step: FlowStepData, steps: FlowStepData[]) => number;
export const getStepIndex: GetStepIndex = (step, steps) => max(indexOf(step, steps), 0);

type GetFinalStepIndex = (steps: FlowStepData[]) => number;
export const getFinalStepIndex: GetFinalStepIndex = (steps) => {
	let index = findIndex(propEq('finalStep', true))(steps);
	if (equals(index, -1)) {
		index = steps.length;
	}

	return index;
};

type GetPreviousStepForKey = (steps: FlowStepData[], stepKey: string) => FlowStepData;
export const getPreviousStepForKey: GetPreviousStepForKey = (steps = [], stepKey: string) => {
	const indexOfCurrentStep = findIndex(propEq('stepKey', stepKey), steps);
	const indexOfPreviousStep = max(indexOfCurrentStep - 1, 0);
	return steps[indexOfPreviousStep];
};

type GetNextStepForKey = (
	steps: FlowStepData[],
	flowId: string,
	stepKey: string,
	formData: FormData,
	paymentWaived?: boolean
) => FlowStepData;
export const getNextStepForKey: GetNextStepForKey = (
	steps = [],
	flowId,
	stepKey,
	formData,
	paymentWaived = false
) => {
	const indexOfCurrentStep = findIndex(propEq('stepKey', stepKey), steps);

	const currentStep = steps[indexOfCurrentStep];

	const nextStepKey = currentStep?.bailout && currentStep.bailout(formData, flowId);

	let indexOfNextStep = nextStepKey
		? findIndex(propEq('stepKey', nextStepKey), steps)
		: indexOfCurrentStep + 1;
	if (paymentWaived) {
		// Since the payment got waived, must check if any subsequent routes should be skipped
		for (let i = indexOfNextStep; i < steps.length; i++) {
			if (steps[i]?.skipIfWaived && !steps[i]?.finalStep) {
				indexOfNextStep = i + 1;
			} else {
				break;
			}
		}
	}
	return steps[indexOfNextStep];
};

interface IMembershipOnboardEmailCheckProps {
	email: string;
	legalFirstName: string;
	legalLastName: string;
	preferredFirstName?: string;
	preferredLastName?: string;
	receiveNewsletter?: boolean;
	supportRegister?: string;
}
export const membershipOnboardEmailCheck = async ({
	email,
	legalFirstName,
	legalLastName,
	preferredFirstName,
	preferredLastName,
	receiveNewsletter,
	supportRegister,
}: IMembershipOnboardEmailCheckProps) => {
	try {
		await axios.post(ApiEndpoints.membershipOnboarding, {
			email,
			legalFirstName,
			legalLastName,
			affirmingFirstName: preferredFirstName,
			affirmingLastName: preferredLastName,
			receiveNewsletter,
			supportRegister,
		});
	} catch (e) {
		console.log(e);
	}
};

export const resendRegistrationCode = async (email: string, supportRegister?: string) => {
	try {
		await axios.post(ApiEndpoints.resendRegistrationCode, {
			email,
			supportRegister,
		});
	} catch (e) {
		console.log(e);
	}
};

type GetFirstUnsubmittedStep = (steps: FlowStepData[], formData: FormData) => FlowStepData;
export const getFirstUnsubmittedStep: GetFirstUnsubmittedStep = (steps, formData) => {
	return steps.find((it) => !formData[it.stepKey]) ?? steps[steps.length - 1];
};

type GetFirstStep = (steps: FlowStepData[]) => FlowStepData | undefined;
export const getFirstStep: GetFirstStep = (steps) => head(steps);

type GetLastStep = (steps: FlowStepData[]) => FlowStepData | undefined;
export const getLastStep: GetLastStep = (steps) => last(steps);

type GetFirstSecureStep = (steps: FlowStepData[]) => FlowStepData;
export const getFirstSecureStep: GetFirstSecureStep = (steps) =>
	steps.find((step) => step.secure) ?? steps[0];

type GetFirstPostOnboardingStep = (steps: FlowStepData[]) => FlowStepData;
export const getFirstPostOnboardingStep: GetFirstPostOnboardingStep = (steps) =>
	steps.find((step) => !step.onboardingStep) ?? steps[0];

type GetIntermediateSteps = (steps: FlowStepData[]) => FlowStepData[];
export const getIntermediateSteps: GetIntermediateSteps = (steps) =>
	steps.filter((step) => step.intermediate || step.returnKey);

type GetRealFlowId = (
	flowId: FlowIdTypes,
	existingFormData: Record<string, unknown>
) => FlowIdTypes;
export const getRealFlowId: GetRealFlowId = (flowId, existingFormData) => {
	const id = cond<FlowIdTypes, FlowIdTypes>([
		[equals('hrt_t_sub'), always('hrt_t')],
		[equals('hrt_e_sub'), always('hrt_e')],
		[equals('followup'), always('gaht')],
		[
			equals('mh'),
			always(
				getServiceType(existingFormData?.[mentalHealthTopicConstants.TOPIC_ID] as MHTopic, {
					uppercase: true,
					style:
						existingFormData?.[mentalHealthTopicConstants.TOPIC_ID] === MHTopic.MENTAL_HEALTH_LETTER
							? 'underscored'
							: 'abbreviated',
				})
			),
		],
		[
			equals('mh_sub'),
			always(
				getServiceType(existingFormData?.[mentalHealthTopicConstants.TOPIC_ID] as MHTopic, {
					uppercase: true,
					style:
						existingFormData?.[mentalHealthTopicConstants.TOPIC_ID] === MHTopic.MENTAL_HEALTH_LETTER
							? 'underscored'
							: 'abbreviated',
				})
			),
		],
		[T, always(flowId)],
	])(flowId);

	return id;
};
